/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║  HEROSTATS.TSX - THE COMMAND CENTER                                       ║
 * ║  Ultra-prominent timer and stats display                                  ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { formatPAW } from '../utils/format';
import { fetchPAWPrice, formatPAWToUSD } from '../utils/price';
import { getReaperPrice, buyReaperKey } from '../utils/contract';
import { toast } from 'react-hot-toast';

interface HeroStatsProps {
  timeRemaining: number;
  pot: bigint;
  jackpotPool: bigint;
  currentPrice: bigint;
  lastBuyer: string;
}

type TimerState = 'safe' | 'warning' | 'danger' | 'critical' | 'final' | 'ended';

// ═══════════════════════════════════════════════════════════════════════════
// HELPER FUNCTIONS
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Determines timer state based on remaining seconds
 * -1 = loading state, treat as safe (will be updated when data loads)
 */
function getTimerState(seconds: number): TimerState {
  if (seconds < 0) return 'safe';          // Loading state (-1)
  if (seconds === 0) return 'ended';
  if (seconds < 60) return 'final';
  if (seconds < 300) return 'critical';
  if (seconds < 1800) return 'danger';
  if (seconds < 3600) return 'warning';
  return 'safe';
}

/**
 * Formats seconds into HH:MM:SS components
 * -1 = loading state, show dashes
 */
function formatTime(totalSeconds: number): { h: string; m: string; s: string } {
  if (totalSeconds < 0) {
    // Loading state - show dashes
    return { h: '--', m: '--', s: '--' };
  }
  if (totalSeconds === 0) return { h: '00', m: '00', s: '00' };
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return {
    h: h.toString().padStart(2, '0'),
    m: m.toString().padStart(2, '0'),
    s: s.toString().padStart(2, '0'),
  };
}

function truncateAddress(addr: string): string {
  if (!addr || addr === '0x0000000000000000000000000000000000000000') return 'Waiting...';
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

const STATE_COLORS = {
  safe: { primary: '#00ff88', glow: 'rgba(0, 255, 136, 0.9)', bg: 'rgba(0, 255, 136, 0.08)', shadow: 'rgba(0, 255, 136, 0.6)' },
  warning: { primary: '#ff8c32', glow: 'rgba(255, 140, 50, 0.9)', bg: 'rgba(255, 140, 50, 0.08)', shadow: 'rgba(255, 140, 50, 0.6)' },
  danger: { primary: '#ff6600', glow: 'rgba(255, 102, 0, 0.9)', bg: 'rgba(255, 102, 0, 0.08)', shadow: 'rgba(255, 102, 0, 0.6)' },
  critical: { primary: '#ff3300', glow: 'rgba(255, 51, 0, 1.0)', bg: 'rgba(255, 51, 0, 0.12)', shadow: 'rgba(255, 51, 0, 0.7)' },
  final: { primary: '#ff0000', glow: 'rgba(255, 0, 0, 1.0)', bg: 'rgba(255, 0, 0, 0.15)', shadow: 'rgba(255, 0, 0, 0.8)' },
  ended: { primary: '#00ff88', glow: 'rgba(0, 255, 136, 0.9)', bg: 'rgba(0, 255, 136, 0.08)', shadow: 'rgba(0, 255, 136, 0.6)' },
};

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function HeroStats({ 
  timeRemaining, 
  pot, 
  jackpotPool, 
  currentPrice, 
  lastBuyer
}: HeroStatsProps) {
  const [pawPrice, setPawPrice] = useState<number>(0);
  const [reaperPrice, setReaperPrice] = useState<bigint>(0n);
  const [isReaperLoading, setIsReaperLoading] = useState(false);
  
  // Fetch PAW price on mount and every 30 seconds (more frequent for better accuracy)
  useEffect(() => {
    const updatePrice = async () => {
      try {
        const price = await fetchPAWPrice();
        console.log('Fetched PAW price:', price);
        // Use fallback price if API fails
        setPawPrice(price > 0 ? price : 0.00000000606); // PAW price fallback
      } catch (error) {
        console.error('Failed to fetch PAW price:', error);
        // Keep existing price if fetch fails
      }
    };
    
    updatePrice();
    const interval = setInterval(updatePrice, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, []);
  
  // Fetch Reaper price every 3 seconds
  useEffect(() => {
    const updateReaperPrice = async () => {
      const price = await getReaperPrice();
      setReaperPrice(price);
    };
    
    updateReaperPrice();
    const interval = setInterval(updateReaperPrice, 3000);
    return () => clearInterval(interval);
  }, []);
  
  const handleBuyReaper = async () => {
    if (timeRemaining < 60) return;
    
    if (!window.confirm(`⚠️ REAPER KEY ⚠️\n\nThis will SLASH 30 seconds from the timer!\n\nCost: ${formatPAW(reaperPrice)} PAW\n\nProceed?`)) return;
    
    setIsReaperLoading(true);
    const toastId = toast.loading('Summoning the Reaper... 💀');
    
    try {
      const success = await buyReaperKey();
      if (success) {
        toast.success('REAPER KEY ACTIVATED! -30 SECONDS 🔪', { id: toastId });
      } else {
        toast.error('Failed to activate Reaper Key', { id: toastId });
      }
    } catch (e) {
      console.error(e);
      toast.error('Reaper Key failed', { id: toastId });
    } finally {
      setIsReaperLoading(false);
    }
  };
  
  const state = getTimerState(timeRemaining);
  const time = formatTime(timeRemaining);
  const colors = STATE_COLORS[state];
  
  // Calculate USD values
  const potUSD = pawPrice > 0 ? formatPAWToUSD(pot, pawPrice) : null;
  const jackpotUSD = pawPrice > 0 ? formatPAWToUSD(jackpotPool, pawPrice) : null;
  const priceUSD = pawPrice > 0 ? formatPAWToUSD(currentPrice, pawPrice) : null;
  
  return (
    <div className="relative">
      {/* Subtle background glow */}
      <div 
        className="absolute -inset-10 blur-[100px] opacity-15 transition-all duration-700"
        style={{ background: `radial-gradient(ellipse at center, ${colors.glow}, transparent 60%)` }}
      />
      
      <motion.div 
        className="relative glass-panel-3d overflow-hidden"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        style={{
          boxShadow: `
            0 8px 32px rgba(0, 0, 0, 0.9),
            0 0 60px ${colors.shadow}40,
            inset 0 1px 0 rgba(0, 255, 255, 0.1),
            inset 0 -1px 0 rgba(255, 0, 128, 0.1)
          `,
          borderColor: `${colors.primary}50`,
        }}
      >
        
        {/* Animated top line */}
        <div className="absolute top-0 left-0 right-0 h-[2px] overflow-hidden">
          <motion.div
            className="h-full w-1/3"
            style={{ 
              background: `linear-gradient(90deg, transparent, ${colors.primary}, ${colors.primary}80, transparent)`,
              boxShadow: `0 0 20px ${colors.glow}`
            }}
            animate={{ x: ['-100%', '400%'] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
          />
        </div>
        
        <div className="p-3 sm:p-4 md:p-8 lg:p-12">
          {/* ═══════════════════════════════════════════════════════════════
              MASSIVE TIMER - THE CENTERPIECE
              ═══════════════════════════════════════════════════════════════ */}
          <div className="text-center mb-4 md:mb-8">
            {/* Label - hide on very small screens */}
            <motion.div 
              className="hidden sm:inline-flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full"
              style={{ 
                background: `${colors.primary}15`,
                border: `1px solid ${colors.primary}40`,
              }}
              animate={state === 'critical' || state === 'final' ? { scale: [1, 1.05, 1] } : {}}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: colors.primary }}>
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="4 2"/>
                <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/>
              </svg>
              <span className="font-rajdhani text-xs sm:text-sm tracking-[0.2em] sm:tracking-[0.4em] uppercase" style={{ color: colors.primary }}>
                TIME TO THE END
              </span>
            </motion.div>
            
            {/* THE GIGANTIC TIMER */}
            <div className="flex items-center justify-center gap-1 sm:gap-2 md:gap-4">
              {/* HOURS */}
              <div className="flex flex-col items-center">
                <div className="flex gap-0.5 sm:gap-1 md:gap-2">
                  <TimerDigit value={time.h[0]} colors={colors} />
                  <TimerDigit value={time.h[1]} colors={colors} />
                </div>
                <span className="text-gray-500 font-rajdhani text-[8px] sm:text-xs tracking-widest mt-1 sm:mt-3">HRS</span>
              </div>
              
              <Separator colors={colors} />
              
              {/* MINUTES */}
              <div className="flex flex-col items-center">
                <div className="flex gap-0.5 sm:gap-1 md:gap-2">
                  <TimerDigit value={time.m[0]} colors={colors} />
                  <TimerDigit value={time.m[1]} colors={colors} />
                </div>
                <span className="text-gray-500 font-rajdhani text-[8px] sm:text-xs tracking-widest mt-1 sm:mt-3">MIN</span>
              </div>
              
              <Separator colors={colors} />
              
              {/* SECONDS */}
              <div className="flex flex-col items-center">
                <div className="flex gap-0.5 sm:gap-1 md:gap-2">
                  <TimerDigit value={time.s[0]} colors={colors} isSeconds />
                  <TimerDigit value={time.s[1]} colors={colors} isSeconds />
                </div>
                <span className="text-gray-500 font-rajdhani text-[8px] sm:text-xs tracking-widest mt-1 sm:mt-3">SEC</span>
              </div>
            </div>
            
            {/* Last Buyer - more compact on mobile */}
            {lastBuyer && lastBuyer !== '0x0000000000000000000000000000000000000000' && (
              <motion.div 
                className="mt-4 sm:mt-6 inline-flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl bg-black/30 border border-neon-cyan/30"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <svg viewBox="0 0 24 24" fill="none" className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-400">
                  <path d="M3 18l2-10 5 4 2-8 2 8 5-4 2 10H3z" fill="currentColor"/>
                </svg>
                <span className="text-gray-400 font-rajdhani text-xs sm:text-sm">Leader</span>
                <span className="font-mono text-neon-cyan font-bold text-xs sm:text-sm">{truncateAddress(lastBuyer)}</span>
              </motion.div>
            )}
            
            {/* ROUND ENDING MESSAGE - Shows when timer is 0 */}
            {state === 'ended' && (
              <motion.div
                className="mt-6 inline-flex items-center gap-3 px-6 py-3 rounded-xl"
                style={{ 
                  background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2), rgba(245, 158, 11, 0.1))',
                  border: '2px solid rgba(234, 179, 8, 0.5)',
                  boxShadow: '0 0 30px rgba(234, 179, 8, 0.3)'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ 
                  opacity: 1, 
                  scale: 1,
                  boxShadow: [
                    '0 0 30px rgba(234, 179, 8, 0.3)',
                    '0 0 50px rgba(234, 179, 8, 0.5)',
                    '0 0 30px rgba(234, 179, 8, 0.3)'
                  ]
                }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <svg className="animate-spin h-5 w-5 text-yellow-400" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                <span className="font-rajdhani font-bold text-lg text-yellow-400 tracking-wider">
                  ROUND ENDING...
                </span>
                <span className="text-yellow-200/70 text-sm">
                  New game starts in ~1 minute
                </span>
              </motion.div>
            )}
          </div>
            
            {/* ═══════════════════════════════════════════════════════════════
                JACKPOT & REAPER - SMALL SQUARES ABOVE PRIZE CARDS
                ═══════════════════════════════════════════════════════════════ */}
            <div className="flex gap-3 md:gap-8 mt-6 md:mt-8 w-full justify-between">
              {/* JACKPOT BOX - LEFT */}
              <motion.div
                className="aspect-square w-20 sm:w-24 md:w-28 flex-shrink-0"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div
                  className="relative rounded-2xl md:rounded-3xl p-2 md:p-3 flex flex-col items-center justify-center w-full h-full overflow-hidden"
                  style={{
                    background: 'linear-gradient(135deg, rgba(234, 179, 8, 0.2) 0%, rgba(245, 158, 11, 0.12) 50%, rgba(217, 119, 6, 0.2) 100%)',
                    border: '2px solid rgba(234, 179, 8, 0.8)',
                    boxShadow: `
                      0 0 0 2px rgba(234, 179, 8, 0.4),
                      0 0 40px rgba(234, 179, 8, 0.4),
                      0 12px 24px rgba(0,0,0,0.6),
                      inset 0 3px 0 rgba(255,255,255,0.15),
                      inset 0 -3px 0 rgba(0,0,0,0.3)
                    `,
                  }}
                >
                  <motion.div
                    className="text-lg md:text-2xl mb-1"
                    animate={{
                      rotate: [0, -5, 5, -5, 0],
                      scale: [1, 1.05, 1],
                    }}
                    transition={{ duration: 3, repeat: Infinity, repeatDelay: 5 }}
                  >
                    🎰
                  </motion.div>
                  <div className="text-center">
                    <div className="font-orbitron text-[10px] md:text-sm text-yellow-400 tracking-wider mb-0.5">
                      JACKPOT
                    </div>
                    <div className="space-y-0.5">
                      <div className="flex items-baseline gap-1 justify-center">
                        <span className="font-mono text-sm md:text-lg font-bold text-yellow-300">
                          ${jackpotUSD || '0.00'}
                        </span>
                      </div>
                      <div className="flex items-baseline gap-1 justify-center">
                        <span className="font-mono text-[8px] md:text-[10px] font-bold text-yellow-200">
                          {formatPAW(jackpotPool)}
                        </span>
                        <span className="text-yellow-400/60 font-rajdhani text-[8px] md:text-[10px]">PAW</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* CENTER SPACER - Hidden chaos mechanics */}
              <div className="flex-1" />
              
              {/* REAPER KEY BOX - RIGHT */}
              <motion.div
                className="aspect-square w-20 sm:w-24 md:w-28 flex-shrink-0"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <motion.button
                  onClick={handleBuyReaper}
                  disabled={timeRemaining < 60 || isReaperLoading}
                  className={`
                    group relative p-2 md:p-3 rounded-2xl md:rounded-3xl overflow-hidden w-full h-full
                    transition-all duration-300
                    ${timeRemaining < 60 
                      ? 'opacity-40 cursor-not-allowed grayscale' 
                      : 'hover:scale-105'
                    }
                  `}
                  style={{
                    background: 'linear-gradient(135deg, rgba(127,29,29,0.95) 0%, rgba(69,10,10,0.98) 100%)',
                    boxShadow: timeRemaining >= 60 
                      ? `
                        0 0 0 2px rgba(239,68,68,0.7),
                        0 0 50px rgba(239,68,68,0.5),
                        0 12px 24px rgba(0,0,0,0.6),
                        inset 0 3px 0 rgba(255,255,255,0.12),
                        inset 0 -3px 0 rgba(0,0,0,0.4)
                      ` 
                      : 'none',
                    border: '2px solid rgba(239,68,68,0.8)',
                  }}
                  whileHover={timeRemaining >= 60 ? { 
                    boxShadow: '0 0 30px rgba(239,68,68,0.5), inset 0 1px 0 rgba(255,255,255,0.12)',
                  } : {}}
                  whileTap={timeRemaining >= 60 ? { scale: 0.98 } : {}}
                  animate={timeRemaining >= 60 ? {
                    boxShadow: [
                      '0 0 18px rgba(239,68,68,0.2)',
                      '0 0 30px rgba(239,68,68,0.3)',
                      '0 0 18px rgba(239,68,68,0.2)',
                    ],
                  } : {}}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  {/* Animated background pulse */}
                  <div className="absolute inset-0 bg-gradient-to-r from-red-600/0 via-red-500/20 to-red-600/0 animate-pulse" />
                  {/* Content */}
                  <div className="relative flex flex-col items-center justify-center h-full gap-0.5 md:gap-1">
                    {/* Skull Icon */}
                    <div className="text-lg md:text-xl">💀</div>
                    {/* Text */}
                    <div className="text-center">
                      <div className="font-orbitron text-[10px] md:text-xs text-red-200 tracking-wider group-hover:text-white transition-colors">
                        REAPER
                      </div>
                      <div className="px-1 py-0.5 bg-red-900/80 rounded border border-red-500 text-red-100 font-bold text-[10px] md:text-xs shadow-lg shadow-red-900/50 mt-0.5">
                        -30s
                      </div>
                      <div className="text-[8px] md:text-[10px] font-mono text-red-300 group-hover:text-red-100 transition-colors mt-0.5">
                        {reaperPrice > 0n ? formatPAW(reaperPrice) : '...'} PAW
                      </div>
                    </div>
                  </div>
                  {/* Danger indicator */}
                  {timeRemaining < 60 && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <span className="text-gray-400 font-rajdhani text-[8px] md:text-[10px]">SAFE ZONE</span>
                    </div>
                  )}
                </motion.button>
              </motion.div>
            </div>
            
            {/* ═══════════════════════════════════════════════════════════════
                PRIZE POOL & KEY PRICE CARDS - BELOW JACKPOT/REAPER
                ═══════════════════════════════════════════════════════════════ */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* PRIZE POOL CARD */}
              <PrizeCard
                label="TOTAL POT"
                value={formatPAW(pot)}
                unit="PAW"
                usdValue={potUSD || undefined}
                gradient="from-emerald-400 via-green-500 to-teal-500"
                glowColor="rgba(0, 255, 128, 0.5)"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" fill="currentColor"/>
                  </svg>
                }
              />
              
              {/* KEY PRICE CARD */}
              <PrizeCard
                label="KEY PRICE"
                value={formatPAW(currentPrice)}
                unit="PAW"
                usdValue={priceUSD || undefined}
                gradient="from-fuchsia-500 via-purple-600 to-violet-700"
                glowColor="rgba(192, 38, 211, 0.5)"
                icon={
                  <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
                    <path d="M15 7l5 5-5 5V7z" fill="currentColor"/>
                    <path d="M4 12h11" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                }
              />
            </div>
          </div>
        
        {/* Bottom gradient line */}
        <div 
          className="h-px"
          style={{ 
            background: `linear-gradient(90deg, transparent, ${colors.primary}, transparent)`,
          }}
        />
      </motion.div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TIMER DIGIT - 3D NIXIE TUBE WITH REAL DEPTH
// ═══════════════════════════════════════════════════════════════════════════

interface TimerDigitProps {
  value: string;
  colors: typeof STATE_COLORS['safe'];
  isSeconds?: boolean;
}

function TimerDigit({ value, colors, isSeconds }: TimerDigitProps) {
  return (
    <motion.div
      className="relative"
      animate={isSeconds ? { 
        filter: ['brightness(1)', 'brightness(1.15)', 'brightness(1)'],
      } : {}}
      transition={{ duration: 0.5, repeat: Infinity }}
    >
      {/* NIXIE TUBE CONTAINER WITH 3D DEPTH */}
      <div 
        className="relative w-[2.5rem] h-[3.5rem] sm:w-[3.5rem] sm:h-[5rem] md:w-[5rem] md:h-[7rem] lg:w-[7.5rem] lg:h-[10.5rem]"
        style={{
          perspective: '500px',
          perspectiveOrigin: 'center center',
        }}
      >
        {/* Outer glass tube shell - 3D transformed */}
        <div
          className="absolute inset-0 rounded-2xl"
          style={{
            background: `
              linear-gradient(180deg, 
                rgba(30, 30, 40, 0.95) 0%, 
                rgba(10, 10, 20, 0.98) 20%,
                rgba(5, 5, 15, 1) 50%,
                rgba(10, 10, 20, 0.98) 80%,
                rgba(30, 30, 40, 0.95) 100%
              )
            `,
            boxShadow: `
              0 8px 32px rgba(0, 0, 0, 0.8),
              0 2px 8px rgba(0, 0, 0, 0.9),
              inset 0 0 60px rgba(0, 0, 0, 0.8),
              inset 0 2px 0 rgba(255, 255, 255, 0.08),
              inset 0 -2px 0 rgba(0, 0, 0, 0.5)
            `,
            border: `2px solid rgba(60, 60, 80, 0.6)`,
            transform: 'translateZ(0)',
          }}
        />
        
        {/* Inner dark cavity - creates depth illusion */}
        <div 
          className="absolute inset-[6px] rounded-xl"
          style={{
            background: `
              radial-gradient(ellipse 80% 60% at 50% 40%, 
                rgba(0, 0, 0, 0.3) 0%, 
                rgba(0, 0, 0, 0.95) 100%
              )
            `,
            boxShadow: `
              inset 0 8px 24px rgba(0, 0, 0, 0.9),
              inset 0 -4px 12px rgba(0, 0, 0, 0.6),
              inset 4px 0 12px rgba(0, 0, 0, 0.6),
              inset -4px 0 12px rgba(0, 0, 0, 0.6)
            `,
          }}
        />
        
        {/* Glass tube curved highlight - left edge */}
        <div 
          className="absolute left-[3px] top-3 bottom-3 w-[6px] rounded-l-xl pointer-events-none"
          style={{
            background: 'linear-gradient(90deg, rgba(255,255,255,0.12), rgba(255,255,255,0.02), transparent)',
          }}
        />
        
        {/* Glass tube curved highlight - right edge (subtle) */}
        <div 
          className="absolute right-[3px] top-3 bottom-3 w-[3px] rounded-r-xl pointer-events-none"
          style={{
            background: 'linear-gradient(270deg, rgba(255,255,255,0.04), transparent)',
          }}
        />
        
        {/* Top glass rim reflection */}
        <div 
          className="absolute top-[3px] left-3 right-3 h-[8px] rounded-t-lg pointer-events-none"
          style={{
            background: 'linear-gradient(180deg, rgba(255,255,255,0.08), transparent)',
          }}
        />
        
        {/* Wire mesh/grid behind digit (cathode structure) */}
        <div 
          className="absolute inset-4 rounded-lg pointer-events-none"
          style={{
            opacity: 0.15,
            backgroundImage: `
              linear-gradient(0deg, transparent 48%, rgba(80,80,100,0.4) 50%, transparent 52%),
              linear-gradient(90deg, transparent 48%, rgba(80,80,100,0.4) 50%, transparent 52%)
            `,
            backgroundSize: '10px 10px',
          }}
        />
        
        {/* THE GLOWING DIGIT with 3D depth */}
        <div className="absolute inset-0 flex items-center justify-center">
          
          {/* Deep shadow layer - appears to be inside the tube */}
          <span
            className="absolute"
            style={{ 
              fontFamily: '"Orbitron", sans-serif',
              fontSize: 'clamp(1.5rem, 8vw, 5rem)',
              fontWeight: 700,
              color: 'rgba(0, 0, 0, 0.8)',
              transform: 'translate(4px, 6px) scale(1.02)',
              filter: 'blur(8px)',
            }}
          >
            {value}
          </span>
          
          {/* Outer bloom glow - atmospheric light scatter */}
          <span
            className="absolute"
            style={{ 
              fontFamily: '"Orbitron", sans-serif',
              fontSize: 'clamp(1.5rem, 8vw, 5rem)',
              fontWeight: 700,
              color: colors.primary,
              filter: 'blur(20px)',
              opacity: 0.4,
            }}
          >
            {value}
          </span>
          
          {/* Mid glow layer - gas ionization effect */}
          <span
            className="absolute"
            style={{ 
              fontFamily: '"Orbitron", sans-serif',
              fontSize: 'clamp(1.5rem, 8vw, 5rem)',
              fontWeight: 700,
              color: colors.primary,
              filter: 'blur(8px)',
              opacity: 0.7,
            }}
          >
            {value}
          </span>
          
          {/* Inner bright core glow */}
          <span
            className="absolute"
            style={{ 
              fontFamily: '"Orbitron", sans-serif',
              fontSize: 'clamp(1.5rem, 8vw, 5rem)',
              fontWeight: 700,
              color: colors.primary,
              filter: 'blur(2px)',
              opacity: 0.9,
            }}
          >
            {value}
          </span>
          
          {/* Main digit - sharp bright filament */}
          <motion.span
            animate={{
              textShadow: [
                `0 0 10px ${colors.glow}, 0 0 30px ${colors.primary}90, 0 0 50px ${colors.primary}60, 0 0 80px ${colors.primary}30`,
                `0 0 15px ${colors.glow}, 0 0 35px ${colors.primary}95, 0 0 60px ${colors.primary}70, 0 0 100px ${colors.primary}40`,
                `0 0 10px ${colors.glow}, 0 0 30px ${colors.primary}90, 0 0 50px ${colors.primary}60, 0 0 80px ${colors.primary}30`,
              ],
            }}
            transition={{ duration: 1.5 + Math.random() * 0.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ 
              fontFamily: '"Orbitron", sans-serif',
              fontSize: 'clamp(1.5rem, 8vw, 5rem)',
              fontWeight: 700,
              color: '#ffffff',
              textShadow: `
                0 0 5px #ffffff,
                0 0 10px ${colors.glow},
                0 0 30px ${colors.primary}90,
                0 0 50px ${colors.primary}60,
                0 0 80px ${colors.primary}30
              `,
            }}
          >
            {value}
          </motion.span>
        </div>
        
        {/* Top cap / electrode with 3D bevel */}
        <div 
          className="absolute -top-[4px] sm:-top-[6px] left-1/2 -translate-x-1/2 w-[70%] max-w-[52px] h-[6px] sm:h-[10px] rounded-t-lg"
          style={{
            background: 'linear-gradient(180deg, #4a4550 0%, #2a2530 40%, #1a1520 100%)',
            boxShadow: '0 -2px 6px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 2px rgba(0,0,0,0.3)',
          }}
        />
        
        {/* Bottom base / socket with 3D bevel */}
        <div 
          className="absolute -bottom-[8px] sm:-bottom-[12px] left-1/2 -translate-x-1/2 w-[80%] max-w-[60px] h-[12px] sm:h-[18px] rounded-b-xl"
          style={{
            background: 'linear-gradient(180deg, #3a3540 0%, #2a2530 30%, #1a1520 70%, #0a0510 100%)',
            boxShadow: '0 6px 12px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 -2px 4px rgba(0,0,0,0.4)',
          }}
        >
          {/* Socket pins - hide on mobile */}
          <div className="hidden sm:block absolute bottom-0 left-4 w-[6px] h-[8px] rounded-b-sm" style={{ background: 'linear-gradient(180deg, #555, #333)' }} />
          <div className="hidden sm:block absolute bottom-0 right-4 w-[6px] h-[8px] rounded-b-sm" style={{ background: 'linear-gradient(180deg, #555, #333)' }} />
          <div className="hidden sm:block absolute bottom-0 left-1/2 -translate-x-1/2 w-[6px] h-[6px] rounded-b-sm" style={{ background: 'linear-gradient(180deg, #555, #333)' }} />
        </div>
        
        {/* Light bleeding onto glass from digit */}
        <div 
          className="absolute inset-2 rounded-xl pointer-events-none"
          style={{
            background: `radial-gradient(ellipse 70% 50% at 50% 50%, ${colors.primary}15 0%, transparent 70%)`,
          }}
        />
        
        {/* Reflected glow on table/surface below */}
        <div 
          className="absolute -bottom-10 left-1/2 -translate-x-1/2 w-[120%] h-10 blur-lg pointer-events-none"
          style={{ 
            background: `radial-gradient(ellipse 60% 100% at 50% 0%, ${colors.primary}40 0%, transparent 70%)`,
          }}
        />
      </div>
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SEPARATOR - Colon dots for timer
// ═══════════════════════════════════════════════════════════════════════════

function Separator({ colors }: { colors: typeof STATE_COLORS['safe'] }) {
  return (
    <motion.div
      className="flex flex-col gap-2 sm:gap-4 md:gap-6 lg:gap-8 items-center mx-0.5 sm:mx-1 md:mx-2 lg:mx-3"
      animate={{ opacity: [1, 0.3, 1] }}
      transition={{ duration: 1, repeat: Infinity }}
    >
      <div 
        className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-3 md:h-3 lg:w-4 lg:h-4 rounded-full"
        style={{ 
          background: colors.primary,
          boxShadow: `0 0 10px ${colors.glow}, 0 2px 4px rgba(0,0,0,0.6)`
        }}
      />
      <div 
        className="w-1.5 h-1.5 sm:w-2 sm:h-2 md:w-3 md:h-3 lg:w-4 lg:h-4 rounded-full"
        style={{ 
          background: colors.primary,
          boxShadow: `0 0 10px ${colors.glow}, 0 2px 4px rgba(0,0,0,0.6)`
        }}
      />
    </motion.div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PRIZE CARD - ULTRA VISIBLE STAT DISPLAY
// ═══════════════════════════════════════════════════════════════════════════

interface PrizeCardProps {
  label: string;
  value: string;
  unit: string;
  usdValue?: string;
  gradient: string;
  glowColor: string;
  icon: React.ReactNode;
}

function PrizeCard({ label, value, unit, usdValue, gradient, glowColor, icon }: PrizeCardProps) {
  return (
    <motion.div
      className="relative p-4 md:p-6 lg:p-8 rounded-2xl md:rounded-3xl overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(10, 10, 30, 0.7) 50%, rgba(0, 0, 0, 0.6) 100%)',
        boxShadow: `
          0 0 0 2px ${glowColor}60,
          0 0 40px ${glowColor},
          0 8px 16px rgba(0,0,0,0.6),
          inset 0 2px 0 rgba(255,255,255,0.1),
          inset 0 -2px 0 rgba(0,0,0,0.3)
        `,
        border: `2px solid ${glowColor}50`,
      }}
      whileHover={{ 
        scale: 1.02,
      }}
    >
      {/* Background gradient glow */}
      <div 
        className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-10`}
      />
      
      {/* Top glow bar */}
      <div 
        className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${gradient}`}
      />
      
      {/* Content */}
      <div className="relative">
        {/* Icon + Label */}
        <div className="flex items-center gap-2 md:gap-3 mb-3 md:mb-4">
          <div className={`p-2 md:p-2.5 rounded-lg md:rounded-xl bg-gradient-to-br ${gradient} bg-opacity-20`}>
            <div className="text-white w-4 h-4 md:w-6 md:h-6">{icon}</div>
          </div>
          <span className="font-rajdhani text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] text-gray-400 uppercase">{label}</span>
        </div>
        
        {/* MASSIVE VALUE */}
        <div className="flex items-baseline gap-2 md:gap-3 flex-wrap">
          <span 
            className={`font-orbitron text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-black bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}
            style={{ textShadow: `0 0 40px ${glowColor}` }}
          >
            {value}
          </span>
          <span className="text-base md:text-lg lg:text-xl text-gray-500 font-rajdhani font-bold">{unit}</span>
        </div>
        
        {/* USD Value */}
        {usdValue && (
          <div className="mt-1 md:mt-2 flex items-center gap-1">
            <span className="text-base md:text-lg lg:text-2xl font-rajdhani font-bold text-green-400">≈ ${usdValue}</span>
            <span className="text-[10px] md:text-xs text-gray-500 font-rajdhani">USD</span>
          </div>
        )}
      </div>
    </motion.div>
  );
}

